import './search.less'

import {Fetcher, m, wrap} from '@codeurs/front'
import {ArrowLinkIcon} from 'assets/inline-icons'
import {Fragment} from 'react'
import ReactPaginate from 'react-paginate'
import {useNavigate} from 'react-router'
import {PageData, PaginationResult, Tag} from 'types'
import {NumberParam, StringParam} from 'use-query-params'
import {t} from 'util/i18n'
import {PageLink} from 'util/pagelink'
import {useFilter} from 'util/params'
import {style} from 'util/style'
import {Trusted} from 'util/trusted'
import {Layout} from 'views/layout'
import {Newsletter} from 'views/partials/newsletter'
import {Theme} from 'views/theme'

type SearchResult = {
  title: string
  text: string
  url: string
}

const styles = style('search', ['isloading'])

export const Search = wrap(Layout)((props: {
  location: Location
  data: PageData & {
    tags: {[group: string]: Array<Tag>}
    results: PaginationResult<SearchResult>
  }
}) => {
  const {url, results, newsletter, language} = props.data
  const filter = useFilter({
    page: NumberParam,
    query: StringParam
  })
  const navigate = useNavigate()
  const buildUrl = page => filter.set({page}).toString()

  return (
    <Fragment>
      <div {...styles}>
        <Theme.Container>
          <h1>{t('search.title')}</h1>
          {filter.query.query && (
            <Fetcher<PaginationResult<SearchResult>>
              url={url}
              data={{json: 'results', ...filter.query}}
              hydrate={results}
            >
              {({data: res, isLoading}) => {
                if (isLoading) return <SearchIsLoading />
                if (!res || res.total === 0)
                  return (
                    <div className={styles('noresults')}>
                      <SearchNoResults />
                    </div>
                  )
                const showArrows = res.per_page && res.per_page < res.total
                return (
                  <div className={styles('resultcontainer')}>
                    <div>
                      {res.data.map(item => {
                        if (!item) return null
                        return (
                          <PageLink to={item.url} class={styles('result')}>
                            <h1 class={styles('result-title')}>
                              <Trusted>{item.title}</Trusted>
                            </h1>
                            <p>
                              <Trusted>{item.text}</Trusted>
                            </p>
                          </PageLink>
                        )
                      })}
                    </div>
                    <ReactPaginate
                      previousLabel={
                        showArrows && (
                          <div className={styles('arrow')}>
                            <ArrowLinkIcon />
                          </div>
                        )
                      }
                      nextLabel={
                        showArrows && (
                          <div className={styles('arrow')}>
                            <ArrowLinkIcon />
                          </div>
                        )
                      }
                      breakLabel={'...'}
                      forcePage={res.current_page - 1}
                      pageCount={res.last_page - 1}
                      marginPagesDisplayed={2}
                      pageRangeDisplayed={5}
                      onPageChange={({selected: page}) =>
                        navigate(buildUrl(page + 1))
                      }
                      containerClassName={styles('pagination').toString()}
                      pageClassName={styles('pagination-page').toString()}
                      previousClassName={styles('pagination-prev').toString()}
                      nextClassName={styles('pagination-next').toString()}
                      breakClassName={styles('pagination-break').toString()}
                      activeClassName="is-active"
                      hrefBuilder={buildUrl}
                    />
                  </div>
                )
              }}
            </Fetcher>
          )}
        </Theme.Container>
      </div>
      <Newsletter {...newsletter} language={language} />
    </Fragment>
  )
})
const SearchNoResults: React.FC = ({...props}) => {
  return <Theme.P>{t('search.noresult')}</Theme.P>
}
const SearchIsLoading: React.FC = ({...props}) => {
  return (
    <div className={styles.isloading}>
      <div className={styles.isloading('block').with('rect1')} />
      <div className={styles.isloading('block').with('rect2')} />
      <div className={styles.isloading('block').with('rect3')} />
      <div className={styles.isloading('block').with('rect4')} />
      <div className={styles.isloading('block').with('rect5')} />
    </div>
  )
}
